/**
 * The optional questions an organizer may show on the guest's details page (batch-29, item R1; plan §3.3). The keys
 * are the ones the booking routine already reads from the guest object. Not in the list, and not offered in the new
 * flow: "Room with", a free age, and every field of the hidden pack. "connecting_rooms" (connecting-rooms S7) is asked only
 * on a booking of two or more rooms, and kept as a wish on the main guest's row.
 */
export const REQUEST_FIELDS = [
  'family_name', 'gender', 'dietary', 'estimated_arrival_window', 'floor_preference', 'proximity_request',
  'airport_pickup_needed', 'accessibility_needs', 'early_checkin', 'late_checkout', 'quiet_room_pref', 'high_floor_pref',
  'pet_traveling', 'first_time_visitor', 'connecting_rooms', 'special_requests',
] as const;
export type RequestField = (typeof REQUEST_FIELDS)[number];

/** What a new event shows until the organizer chooses. */
export const DEFAULT_REQUEST_FIELDS: readonly RequestField[] = ['estimated_arrival_window', 'special_requests'];

/**
 * Asked no more (item BG1, 2026-10-12): a food note per person. Dietary needs go in the booking's special requests, so
 * an event that still has the tick shows "Special requests" in its place. The database keeps the key in its allowed
 * list, and the guests who already gave a note keep it.
 */
export const RETIRED_REQUEST_FIELDS: readonly RequestField[] = ['dietary'];
const IN_PLACE_OF: Partial<Record<RequestField, RequestField>> = { dietary: 'special_requests' };
/** The ticks an organizer is offered: every field but the retired ones. */
export const OFFERED_REQUEST_FIELDS: readonly RequestField[] = REQUEST_FIELDS.filter((k) => !RETIRED_REQUEST_FIELDS.includes(k));

const KNOWN = new Set<string>(REQUEST_FIELDS);
/** The stored list as the page uses it: known keys only, a retired key read as the field that took its place, in the fixed order, each once. */
export function shownRequestFields(stored: readonly string[] | null | undefined): RequestField[] {
  const want = new Set((stored ?? DEFAULT_REQUEST_FIELDS).filter((k): k is RequestField => KNOWN.has(k)).map((k) => IN_PLACE_OF[k] ?? k));
  return REQUEST_FIELDS.filter((k) => want.has(k));
}
